import { useState, useRef } from "react";
import { useTranslation } from "next-i18next";
import cx from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSnapshot } from "valtio";

import { UserIcon } from "common/ui/UserIcon";
import { Paragraph } from "common/ui/Paragraph";
import { Icon, type IconVariants } from "common/ui/Icon";
import { Divider } from "common/ui/Divider";
import { Stack } from "common/ui/Stack";
import { Caption } from "common/ui/Caption";
import { Skeleton } from "common/ui/Skeleton";
import { Label } from "common/ui/Label";
import { Toast, type ToastVariants } from "common/ui/Toast";
import { BrokerFeatureRequestModal } from "common/features/account/components/BrokerFeatureRequestModal";
import { DeveloperFeatureRequestModal } from "common/features/account/components/DeveloperFeatureRequestModal";

import store from "common/middleware/store";
import { useClickOutside, useLoggedInUser } from "common/middleware/hooks";
import { logout } from "common/middleware/api.auth";

import { logoutAndRedirect } from "common/utils/utils.auth";
import { APP_ROUTES } from "common/constants/routes";

import styles from "./UserMenu.module.scss";

export interface UserMenuItem {
  text: string,
  onClick: () => void,
}

export type Portal = "seller"
  | "ambassador"
  | "buyer"
  | "host"
  | "broker"
  | "developer"
  | "auth";

interface Props {
  menuItems?: UserMenuItem[],
  portal: Portal,
}

export default function UserMenu({
  menuItems,
  portal,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBrokerRequestModalOpen, setIsBrokerRequestModalOpen] = useState(false);
  const [isDeveloperRequestModalOpen, setIsDeveloperRequestModalOpen] = useState(false);
  const [toast, setToast] = useState<{
    msg: string,
    variant: ToastVariants,
  }>();

  const { t } = useTranslation("common");
  const { locale, asPath } = useRouter();
  const { isLicensedSeller } = useSnapshot(store);
  const { user, isLoading: isUserLoading } = useLoggedInUser();

  const userMenu = useRef(null);

  useClickOutside(userMenu, () => setIsMenuOpen(false));

  const isUserPartOfPresti = user?.builderUsers.some(b => b.builderId === "1") // TODO Only supporting Presti
  const apps: {
    id: Portal,
    name: string,
    href: string,
    icon: IconVariants,
    isUnlocked: boolean,
    onClick?: () => void,
    isFeatureRequested?: boolean,
    isUnavailable?: boolean,
  }[] = [
    ...user?.canAccessHostSystem ? [{
      id: "host" as Portal,
      name: t("navigationBar.employee"),
      href: `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${process.env.NEXT_PUBLIC_HOST_URL}/${locale}`,
      icon: "shieldPerson" as IconVariants,
      isUnlocked: true,
    }] : [],
    {
      id: "buyer",
      name: t("navigationBar.buyer"),
      href: `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${process.env.NEXT_PUBLIC_BUYER_URL}/${locale}`,
      icon: "shoppingCart",
      isUnlocked: true,
    },
    {
      id: "seller",
      name: t("navigationBar.seller"),
      href: `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${process.env.NEXT_PUBLIC_SELLER_URL}/${locale}`,
      icon: "buildings",
      isUnlocked: true,
      onClick: isLicensedSeller ? logout : undefined,
    },
    {
      id: "developer",
      name: t("navigationBar.developer"),
      href: `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${process.env.NEXT_PUBLIC_ADMIN_URL}/${locale}&builderId=1`, // TODO Only supporting Presti
      icon: "buildings",
      isUnlocked: isUserPartOfPresti, // TODO Only supporting Presti
      isFeatureRequested: false, // TODO Need way to determine request status
      isUnavailable: true, // TODO Feature unavailable for now
    },
    {
      id: "broker",
      name: t("navigationBar.broker"),
      href: `${process.env.NEXT_PUBLIC_AUTH_URL}/redirect?redirect=${process.env.NEXT_PUBLIC_BROKER_URL}/${locale}`,
      icon: "userGroup",
      isUnlocked: user?.brokerProfile?.approved,
      isFeatureRequested: user?.hasBrokerProfile && !user?.brokerProfile?.approved,
    },
    {
      id: "ambassador",
      name: t("navigationBar.ambassador"),
      href: undefined, // TODO Feature unavailable
      icon: "userGroup",
      isUnlocked: false, // TODO Feature unavailable for now
      isUnavailable: true, // TODO Feature unavailable for now
    },
  ];

  const currApp = apps.find(a => a.id === portal);

  return (
    isUserLoading ? (
      <Skeleton
        width={portal === "auth" ? 40 : 150}
        height={40}
        rounded
      />
    ) : (
      <>
        <div
          ref={userMenu}
          className={styles.UserMenu}
        >
          <button
            className={styles.MenuBtn}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {portal !== "auth" && (
              <div className={styles.App}>
                <Icon icon={currApp.icon} />
                <Paragraph
                  variant="p3"
                  className={styles.Text}
                >
                  {currApp.name}
                </Paragraph>
              </div>
            )}
            <UserIcon name={user?.name} />
          </button>
          {isMenuOpen && (
            <div className={styles.Menu}>
              <Stack direction="column" gap={8}>
                <Paragraph variant="p2">
                  {t("navigationBar.useOwnspaceAs")}
                </Paragraph>
                <Divider />
              </Stack>
              <div>
                {apps.map(a => (
                  <div
                    key={a.id}
                    className={styles.AppLinkContainer}
                  >
                    <a
                      className={cx(styles.AppLink, {
                        [styles.Selected]: a.id === portal,
                        [styles.Disabled]: !a.isUnlocked || a.isUnavailable,
                      })}
                      href={a.isUnlocked && a.id !== portal
                        ? a.href
                        : undefined
                      }
                      onClick={a.onClick}
                    >
                      <Icon icon={a.icon} />
                      <Paragraph variant="p2">
                        {a.name}
                      </Paragraph>
                    </a>
                    {!a.isUnlocked && (
                      a.isUnavailable ? (
                        <Caption className={cx(styles.colorSecondary700, styles.textAlignEnd)}>
                          {t("navigationBar.comingSoon")}
                        </Caption>
                      ) : a.isFeatureRequested ? (
                        <Caption className={cx(styles.colorSecondary700, styles.textAlignEnd)}>
                          {t("navigationBar.requested")}
                        </Caption>
                      ) : (
                        <button
                          className={cx(styles.UnlockBtn, styles.textAlignEnd)}
                          onClick={() => {
                            switch(a.id) {
                              case "developer":
                                setIsDeveloperRequestModalOpen(true);
                                return;
                              case "broker":
                                setIsBrokerRequestModalOpen(true);
                                return;
                            }
                          }}
                        >
                          <Caption>
                            {t("navigationBar.unlockNow")}
                          </Caption>
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
              <Divider />
              <div>
                {menuItems?.map(i => (
                  <button
                    key={i.text}
                    className={styles.AdditionalMenuBtn}
                    onClick={() => {
                      i.onClick();
                      setIsMenuOpen(false);
                    }}
                  >
                    <Label variant="l1">
                      {i.text}
                    </Label>
                  </button>
                ))}
                {portal === "auth" ? (
                  <>
                    <Link
                      className={styles.AdditionalMenuBtn}
                      href={APP_ROUTES.PROFILE}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Paragraph variant="p2">
                        {t("navigationBar.viewProfile")}
                      </Paragraph>
                    </Link>
                    <Link
                      className={styles.AdditionalMenuBtn}
                      href={APP_ROUTES.APPOINTMENTS}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Paragraph variant="p2">
                        {t("navigationBar.appointments")}
                      </Paragraph>
                    </Link>
                  </>
                ) : (
                  <>
                    <a
                      className={styles.AdditionalMenuBtn}
                      href={`${process.env.NEXT_PUBLIC_AUTH_URL}/${locale}${APP_ROUTES.PROFILE}`}
                    >
                      <Paragraph variant="p2">
                        {t("navigationBar.viewProfile")}
                      </Paragraph>
                    </a>
                    <a
                      className={styles.AdditionalMenuBtn}
                      href={`${process.env.NEXT_PUBLIC_AUTH_URL}/${locale}${APP_ROUTES.APPOINTMENTS}`}
                    >
                      <Paragraph variant="p2">
                        {t("navigationBar.appointments")}
                      </Paragraph>
                    </a>
                  </>
                )}
                <button
                  className={cx(styles.AdditionalMenuBtn, styles.redText)}
                  onClick={logoutAndRedirect}
                >
                  <Paragraph variant="p2">
                    {t("navigationBar.logout")}
                  </Paragraph>
                </button>
                <Link
                  className={styles.AdditionalMenuBtn}
                  href={asPath}
                  locale={locale === "en-US" ? "fr" : "en-US"}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Paragraph variant="p2">
                    {t("navigationBar.locale")}
                  </Paragraph>
                </Link>
              </div>
            </div>
          )}
        </div>
        {isDeveloperRequestModalOpen && (
          <DeveloperFeatureRequestModal
            close={() => setIsDeveloperRequestModalOpen(false)}
            onSuccess={() => setToast({
              msg: t("navigationBar.developerRequestSuccess"),
              variant: "success",
            })}
          />
        )}
        {isBrokerRequestModalOpen && (
          <BrokerFeatureRequestModal
            close={() => setIsBrokerRequestModalOpen(false)}
            onSuccess={() => setToast({
              msg: t("navigationBar.brokerRequestSuccess"),
              variant: "success",
            })}
          />
        )}
        {toast && (
          <Toast
            close={() => setToast(undefined)}
            timeout={4000}
            variant={toast.variant}
          >
            {toast.msg}
          </Toast>
        )}
      </>
    )
  )
}
